/**
 * The module entitlement surface, as the back office calls it.
 *
 * Two migration 264 reads and one migration 238 write. The write is
 * set_module_entitlement, unchanged since S1-04 — MOD-03 makes enabling and
 * disabling a configuration action, and this module does not add a second way
 * to perform it.
 */
import { supabase } from './supabase';
import type { EntitlementAuditRow, ModuleEntitlementRow } from './elevateEntitlements';

function refuse(error: { message: string; details?: string | null } | null): void {
  if (!error) return;
  throw new Error(error.details ? `${error.message} (${error.details})` : error.message);
}

export async function fetchEntitlementOverview(
  module = 'elevate',
): Promise<ModuleEntitlementRow[]> {
  const { data, error } = await supabase.rpc('module_entitlement_overview', { p_module: module });
  refuse(error);
  return (data ?? []) as ModuleEntitlementRow[];
}

export async function fetchEntitlementAudit(filters?: {
  module?: string | null;
  tenantId?: string | null;
}): Promise<EntitlementAuditRow[]> {
  const { data, error } = await supabase.rpc('module_entitlement_audit', {
    p_module: filters?.module ?? 'elevate',
    p_tenant: filters?.tenantId ?? null,
    p_from: null,
    p_to: null,
  });
  refuse(error);
  return (data ?? []) as EntitlementAuditRow[];
}

/**
 * MOD-03. The window arguments are NOT optional at this call site even though
 * the function defaults them to NULL, because set_module_entitlement upserts
 * every column it is given: omitting them silently clears a stored validity
 * window on the next flip of the enabled switch. Callers pass what they mean.
 */
export async function setModuleEntitlement(input: {
  tenantId: string;
  module?: string;
  enabled: boolean;
  validFrom: string | null;
  validUntil: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('set_module_entitlement', {
    p_tenant: input.tenantId,
    p_module: input.module ?? 'elevate',
    p_enabled: input.enabled,
    p_valid_from: input.validFrom,
    p_valid_until: input.validUntil,
  });
  refuse(error);
}
